import { browser } from "$app/environment";
import type { RestaurantDynamic, RestaurantSemiStatic } from "$lib/def/landing";
import { get$, profile$ } from "$lib/serv/core/auth";
import { createUpgrader, open, transaction } from "$lib/util/db";
import { formatFoodReadyTime, merchantNameSuburbSlug, slugify } from "$lib/util/format";
import { deepCopy } from "$lib/util/object";
import type { Address } from "proto/address";
import type { ImageDetails } from "proto/image";
import type {
	Menu as _Menu,
	Section as _Section,
	Item as _Item,
	Variant as _Variant,
	AddonGroup as _AddonGroup,
	ExtraGroup,
	OptionGroup,
	Addon as _Addon,
} from "proto/menu";
import type { Restaurant, Tag } from "proto/restaurant";
import { EMPTY, from, merge, switchMap, tap, type Observable, of, map, distinctUntilChanged, pairwise } from "rxjs";
import type { WithContext, Restaurant as RestaurantSchema } from 'schema-dts';
import { thumbor } from "./image-details";
import type { Vertical } from "$lib/def/verticals";

const DBNAME = 'restaurants';

const upgrader = createUpgrader({
	1: (db) => [
		db.createObjectStore('menus', { keyPath: 'id' }),
		db.createObjectStore('exposed_restaurants', { keyPath: 'id' }),
	],
})
const pdb = browser ? open(DBNAME, 1, upgrader) : Promise.resolve(null);

/**
 * Clear all object stores from the database.
 * @returns Promise that resolves when all object stores have been cleared.
 */
export async function clearDB() {
	let db: IDBDatabase | null = null;
	try {
		db = await pdb;
	} catch (error) {
		console.warn('Failed to open IDB for clearing.');
	}
	const oss = ['menus', 'exposed_restaurants'];
	if (!db) return;
	const t = transaction(db, oss, 'readwrite');
	Promise.all(oss.map(os => t.os(os).clear())).then(() => console.log(`Cleared [${oss.join(', ')}] of IDB ${DBNAME}.`));
}

/**
 * Clear DB on logout and login.
 */
profile$.pipe(
	map(user => !!user),
	distinctUntilChanged(),
	pairwise(), // effectivley skipping the first emit
).subscribe(() => clearDB());

export type Addon = _Addon & { options: OptionGroup[], extras: ExtraGroup[] };
export type AddonGroup = _AddonGroup & { items: Addon[] };
export type Variant = _Variant & { extras: ExtraGroup[], addons: AddonGroup[] };
export type Item = _Item & { variants: Variant[] };
export type Section = _Section & { items: Item[] };
export type Menu = _Menu & { sections: Section[] };

let _getMenu = (id:number, slug = '', ignoreCache = false) => {
	if (slug) {
		// we do not make use of cache here as this will result in a separate menu stored in memory for every slug in that menu
		return get$<_Menu>(`api://v2/menus/${id}?flattened=true&slug=${encodeURIComponent(slug)}`);
	}
	return getCache<_Menu>(id, 'menus', `api://v2/menus/${id}?flattened=true`, ignoreCache);
}

export function getMenu(id:number, slug?: string, ignoreCache = false) {
	return _getMenu(id, slug, ignoreCache);
}

export function normalise(menu:_Menu) {
	if (!menu) return null;

	const addons = menu.addons?.map(addon => ({
		...deepCopy({}, addon),
		items: addon.items?.map(item => ({
			...deepCopy({}, item),
			...normOptions(item, menu.options),
			...normExtras(item, menu.extras),
		} as Addon))
	}));
	return {
		...deepCopy({}, menu),
		sections: menu.sections?.map(sec => ({
			...deepCopy({}, sec),
			items: sec.items?.map(item => ({
				...deepCopy({}, item),
				section_id: sec.id, // handy (when getting a quote this is needed)
				variants: item.variants?.map(variant => ({
					...deepCopy({}, variant),
					...normOptions(variant, menu.options),
					...normExtras(variant, menu.extras),
					...normAddons(variant, addons),
				} as Variant)),
			} as Item & { section_id: Section["id"] })),
		} as Section))
	} as Menu;
}

export function normOptions<T extends { option_indices?: number[] }>(item:T, options:OptionGroup[]) {
	const out = normIndeces('option_indices', 'options', item, options) as { options?: OptionGroup[] };
	return out;
}

export function normExtras<T extends { extra_indices?: number[] }>(item:T, extras:ExtraGroup[]) {
	return normIndeces('extra_indices', 'extras', item, extras) as { extras?: OptionGroup[] };
}

export function normAddons<T extends { addon_indices?: number[] }>(item:T, addons:AddonGroup[]) {
	return normIndeces('addon_indices', 'addons', item, addons) as { addons?: AddonGroup[] };
}

interface Indexed {
	option_indices?: number[];
	extra_indices?: number[];
	addon_indices?: number[];
}

interface Normalized {
	options?: OptionGroup[];
	extras?: ExtraGroup[];
	addons?: AddonGroup[];
}

function normIndeces<T extends Indexed>(prop: keyof Indexed, normProp: keyof Normalized, item:T, groups: OptionGroup[] | ExtraGroup[] | AddonGroup[]) {
	const out = {} as Normalized;
	if (item[prop]) {
		item[prop]?.forEach(idx => {
			if (!groups[idx]) throw new Error(`Menu malformed. One of the ${normProp}' indeces (${idx}) does not exist in the available list of ${normProp}.`)
		});
		out[normProp] = item[prop]?.map(idx => deepCopy({}, groups[idx])) as any;
		//delete item[prop]; // we no longer need the indeces
	}
	return out;
}

export function getRestaurantExposed(id:number):Observable<RestaurantExposed> {
	return getCache(id, 'exposed_restaurants', `api://exposure/preview/v2/restaurants/${id}?seo=true`);
}

export async function storeRestaurantExposed(rest:RestaurantExposed) {
	const db = await pdb;
	if (!db) return null;
	const os = transaction(db, 'exposed_restaurants', 'readwrite').os('exposed_restaurants');
	return os.put(rest);
}

export async function retrieveRestaurantExposed(id:number) {
	const db = await pdb;
	if (!db) return null;
	const os = transaction(db, 'exposed_restaurants').os('exposed_restaurants');
	return os.get<RestaurantExposed>(id);
}

export type PartRestaurant = Partial<RestaurantExposed & RestaurantSemiStatic & RestaurantDynamic>

export interface RestaurantExposed {
	id: number;
	name: string;
	description: string;
	vertical: string;
	tags: { id:number, name:string, type:'CUISINE'|'DIETARY', description?: string }[];
	images: { restaurant_header: ImageDetails, restaurant_logo: ImageDetails };
	address: {
		street_number: string;
		street_name: string;
		suburb: string;
		town: string;
		province: string;
		postal_code: string;
		latitude: string;
		longitude: string;
	};
	menu_id: number;
	statistics: {
		avg_food_quality_rating: number;
		cnt_food_quality_rating: number;
	};
	// V2 props
	restaurant_group: {
		id: number;
		name: string;
		customer_facing_group_name: string;
	},
	classifications: {
		id: string;
		name: string;
		type: 'primary_food' | 'marked_up_prices' | 'age_restricted' | 'datastore'; // | 'department'?
		vertical: Vertical; // 'restaurant' | 'store'
	}[]
}

/**
 * A basic cache function that fetches fresh data while emmitting the cached data first.
 * @param id Restaurant ID
 * @param objectStoreName IndexedDB object store name
 * @param url The url to use for fetching fresh data
 * @returns A stream of the cached data and fresh data
 */
function getCache<T>(id:number|string, objectStoreName:string, url:string, ignoreCache = false):Observable<T> {
	if (!id) return EMPTY; // todo: of(null)
	const cache$ = from(pdb).pipe(
		switchMap(db => {
			if (!db) return EMPTY; // todo: of(null)
			const os = transaction(db, objectStoreName).os(objectStoreName);
			return from(os.get<T>(id));
		}),
	);
	const fresh$ = get$<T>(url).pipe(
		tap(async res => {
			let db: IDBDatabase | null = null;
			try {
				db = await pdb;
			} catch (error) {
				console.warn('Failed to open IDB for reading restaurant cache.');
			}
			if (!db) return EMPTY; // todo: of(null)
			const os = transaction(db, objectStoreName, 'readwrite').os(objectStoreName);
			os.put(res);
		}),
	);
	if (ignoreCache) return fresh$;
	return merge(cache$, fresh$);
}

/**
 * Basically a subset of the RestaurantExposed type.
 */
export type RestaurantLDJSON = Pick<Restaurant, 'id' | 'name'> & {
	address: Pick<Address, 'latitude' | 'longitude' | 'street_number' | 'street_name' | 'suburb' | 'town' | 'province' | 'postal_code'>;
	tags: Partial<Tag>[];
	images: Partial<Restaurant['images']>;
	statistics: Partial<Restaurant['statistics']>;
};

export function buildLDJSON(restaurant: RestaurantLDJSON, origin:string, menu?: Menu) {
	const jsonld: WithContext<RestaurantSchema> = {
		"@context": "https://schema.org",
		"@type": "Restaurant",
		"name": restaurant.name,
		"url": `${origin}/delivery/restaurant/${merchantNameSuburbSlug(restaurant.name, restaurant.address?.suburb)}/${restaurant.id}`,
		// "menu": "https://www.example.com/menu" // TODO when menu deep link to the app is deployed
	};
	if (restaurant.tags?.length) {
		jsonld.servesCuisine = restaurant.tags.filter(t => t.type === 'CUISINE').map(t => t.name).join(', ')
	}
	if (restaurant.images?.restaurant_header || restaurant.images?.restaurant_logo) {
		const image = [] as string[];
		if (restaurant.images.restaurant_header) {
			const url = thumbor(restaurant.images.restaurant_header, 1200, 0);
			if (url) image.push(url);
		}
		if (restaurant.images.restaurant_logo) {
			const url = thumbor(restaurant.images.restaurant_logo, 64, 0);
			if (url) image.push(url)
		}
		jsonld.image = image;
	}
	if (restaurant.address) {
		const {street_number, street_name, town, province, postal_code} = restaurant.address;
		const str = (street_number && street_name) ? `${street_number} ${street_name}` : street_name;
		jsonld.address = {
			"@type": 'PostalAddress',
			"streetAddress": str,
			"addressLocality": town,
			"addressRegion": province,
			"postalCode": postal_code,
			"addressCountry": 'ZA'
		};
	}
	if (restaurant.address?.latitude && restaurant.address.longitude) {
		jsonld.geo = {
			"@type": "GeoCoordinates",
			"latitude": restaurant.address.latitude,
			"longitude": restaurant.address.longitude
		};
	}
	if (typeof restaurant.statistics?.cnt_food_quality_rating !== 'undefined' && restaurant.statistics.cnt_food_quality_rating > 10) {
		jsonld.aggregateRating = {
			"@type": "AggregateRating",
			"ratingValue": restaurant.statistics.avg_food_quality_rating?.toFixed(2),
			"reviewCount": restaurant.statistics.cnt_food_quality_rating,
		};
	}
	return JSON.stringify(jsonld);
}

export function getRestaurantState(rest: {
	id?: number;
	online?: boolean;
	open?: boolean;
	delivery_option?: 'collect' | 'delivery' | 'collect_and_delivery';
	future_opening_time?: { open_next_message: string };
	prep_time?: number;
	estimated_delivery_time_for_distance?: number;
}) {
	const {id, online, open, delivery_option, future_opening_time, prep_time, estimated_delivery_time_for_distance} = rest;
	const isOffline = typeof online !== 'undefined' && !online;
	const isClosed = typeof open !== 'undefined' && !open;
	const hasCollect = typeof delivery_option !== 'undefined' && (delivery_option === 'collect' || delivery_option === 'collect_and_delivery');
	const hasDelivery = typeof delivery_option !== 'undefined' && (delivery_option === 'delivery' || delivery_option === 'collect_and_delivery');
	let text = '';
	if (isOffline) {
		text = 'Not accepting orders now';
	} else if (isClosed) {
		text = future_opening_time?.open_next_message || 'Be back soon';
	} else if (hasCollect && !hasDelivery) {
		text = 'Collect only';
	} else if (typeof prep_time !== 'undefined' && typeof estimated_delivery_time_for_distance !== 'undefined') {
		text = `Delivery: ${formatFoodReadyTime((prep_time || 0) + (estimated_delivery_time_for_distance || 0))}`;
	} else {
		text = '';
	}
	return {
		id,
		prep_time: rest.prep_time,
		estimated_delivery_time_for_distance: rest.estimated_delivery_time_for_distance,
		hasCollect,
		hasDelivery,
		collect_only: hasCollect && !hasDelivery,
		offline: isOffline || isClosed,
		text
	}
}

// ############################################################
// DB FUNCTIONS
// ############################################################


// async function clear() {
// 	return delDB(DBNAME);
// }

// ############################################################
// MOCK UTILS
// ############################################################

export function mockGetMenu(fn:(id:number) => Observable<_Menu>) {
	_getMenu = fn;
}
